import { useEffect, useRef, useState } from 'react';

interface PendingSince {
  requestId: string;
  at: number;
}

/**
 * Waits on one pending request: reads the step again every `pollMs`, one read at a time, until the
 * request settles, and gives up `waitMs` after the step first saw it pending. Answers whether the
 * wait on `pendingId` ran out.
 *
 * The deadline runs on a timer of its own and abandons the poll's read in flight: a read that never
 * answers would otherwise hold the step waiting for good, since each tick arms the next only once
 * its read has answered. Both timers are torn down with the render that armed them, and the
 * deadline is re-armed at the same moment on every tick, since it counts from when the step first
 * saw the request pending.
 */
export const useRequestWait = ({
  pendingId,
  pollMs,
  waitMs,
  load,
  alive,
}: {
  pendingId: string | null;
  pollMs: number;
  waitMs: number;
  load: (signal?: AbortSignal) => Promise<void>;
  alive: { current: boolean };
}): boolean => {
  const [waitedOut, setWaitedOut] = useState<string | null>(null);
  const since = useRef<PendingSince | null>(null);
  const [tick, setTick] = useState(0);
  const pollRead = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      pollRead.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (pendingId === null || waitedOut === pendingId) {
      return undefined;
    }

    // When the step started waiting on this request: the first time it saw it pending.
    if (since.current?.requestId !== pendingId) {
      since.current = { requestId: pendingId, at: Date.now() };
    }
    const at = since.current.at;
    const overdue = () => Date.now() - at >= waitMs;
    if (overdue()) {
      setWaitedOut(pendingId);
      return undefined;
    }

    const deadline = setTimeout(
      () => {
        pollRead.current?.abort();
        setWaitedOut(pendingId);
      },
      at + waitMs - Date.now(),
    );

    const poll = setTimeout(() => {
      if (overdue()) {
        setWaitedOut(pendingId);
        return;
      }

      const controller = new AbortController();
      pollRead.current = controller;
      void load(controller.signal).then(() => {
        if (alive.current && !controller.signal.aborted) {
          setTick((count) => count + 1);
        }
      });
    }, pollMs);

    return () => {
      clearTimeout(deadline);
      clearTimeout(poll);
    };
  }, [pendingId, waitedOut, pollMs, waitMs, load, alive, tick]);

  return pendingId !== null && waitedOut === pendingId;
};
